export const ROOT = 'root'
export const LAYOUT = 'layout'
export const ROW_BRANCH = 'row-branch'
export const ROW = 'row'
export const COL = 'col'
export const COL_UNIQUE = 'col-unique'
export const COL_SHARED = 'col-shared'
export const PLUGIN = 'plugin'

export function plugin (platform, render, node) {
  if (!render || !node) {
    return
  }

  const plugin = render.plugins[node.pluginId]

  if (!plugin) {
    return
  }

  const module = platform.components[plugin.ref]

  if (module) {
    return module.default
  }
}

export function nodes (render, node) {
  if (!render || !node) {
    return []
  }

  if (node.block && node.block.nodes) {
    const nodes = node.block.nodes

    for (const node of nodes) {
      if (node.block && node.block.id) {
        continue
      }

      let blockId = render.links[node.id]

      if (!blockId) {
        continue
      }

      if (typeof blockId !== 'string') {
        blockId = blockId[node.groupId]

        if (!blockId) {
          return []
        }
      }

      if (!blockId) {
        continue
      }

      node.block = render[node.type === ROW_BRANCH ? 'branches' : 'blocks'][blockId]
    }

    return node.block.nodes
  }

  if (!render.links || !render.blocks) {
    return []
  }

  let blockId = render.links[node.id]

  if (!blockId) {
    return []
  }

  if (typeof blockId !== 'string') {
    blockId = blockId[node.groupId]

    if (!blockId) {
      return []
    }
  }

  const block = render[node.type === ROW_BRANCH ? 'branches' : 'blocks'][blockId]

  if (!block) {
    return []
  }

  return block.nodes || []
}

export function childBlockId (render, node) {
  if (!render || !node) {
    return null
  }

  if (node.block && node.block.id) {
    return node.block.id
  }

  if (!render.links || !render.blocks) {
    return null
  }

  let blockId = render.links[node.id]

  if (!blockId) {
    return null
  }

  if (typeof blockId !== 'string') {
    blockId = blockId[node.groupId]

    if (!blockId) {
      return null
    }
  }

  return blockId
}

export function renderProps (render, node) {
  if (node.type === LAYOUT && node.groupId && node.block && node.block.nodes) {
    setGroupIds(node.block.nodes, node.groupId)
  }

  return {
    render,
    node,
  }
}

export function nodeProps (render, node, additional = {}) {
  const props = {}

  if (node.fields) {
    props.tag = node.fields.tagName
  }

  return {
    ...props,
    ...additional,
  }
}

export function nodeFields (render, node) {
  if (!node || !node.id) {
    return {}
  }

  return render.nodeFields[node.id] || {}
}

export function pluginProps (render, node) {
  if (!render || !node) {
    return {}
  }

  const plugin = render.plugins[node.pluginId]

  if (!plugin) {
    return {}
  }

  const props = {}

  if (plugin.preloadData) {
    props.preloadData = JSON.parse(JSON.stringify(plugin.preloadData))
  }

  if (plugin.fields) {
    props.fields = JSON.parse(JSON.stringify(plugin.fields))
  }

  if (plugin.missingRef) {
    props.missingRef = plugin.missingRef
  }

  return props
}

export function nodeIs (node, ...types) {
  return types.includes(node.type)
}

function setGroupIds (nodes, groupId) {
  for (const node of nodes) {
    if (node.type === LAYOUT) {
      continue
    }

    node.groupId = groupId

    if (node.block && node.block.nodes) {
      setGroupIds(node.block.nodes, groupId)
    }
  }
}

function findBlockIn (block, predicate) {
  if (!block || !block.nodes) {
    return null
  }

  for (const node of block.nodes) {
    if (predicate(node.block)) {
      return node.block
    }

    const found = findBlockIn(node.block, predicate)

    if (found) {
      return found
    }
  }

  return null
}

export function findBlock (render, predicate) {
  if (!predicate) {
    throw new Error('find block requires predicate')
  }

  if (typeof predicate === 'string') {
    if (render.blocks[predicate]) {
      return render.blocks[predicate]
    }

    if (render.branches[predicate]) {
      return render.branches[predicate]
    }

    const id = predicate

    predicate = block => block.id === id
  } else if (typeof predicate === 'function') {
    for (const id in render.blocks) {
      const block = render.blocks[id]

      if (predicate(block)) {
        return block
      }
    }

    for (const id in render.branches) {
      const block = render.branches[id]

      if (predicate(block)) {
        return block
      }
    }
  } else if (typeof predicate === 'object') {
    return predicate[Object.keys(predicate)[0]]
  }

  if (render.layout?.block) {
    if (predicate(render.layout.block)) {
      return render.layout.block
    }

    const found = findBlockIn(render.layout.block, predicate)

    if (found) {
      return found
    }
  }

  for (const [, block] of Object.entries(render.blocks)) {
    const found = findBlockIn(block, predicate)

    if (found) {
      return found
    }
  }

  for (const [, block] of Object.entries(render.branches)) {
    const found = findBlockIn(block, predicate)

    if (found) {
      return found
    }
  }

  return null
}

function findNodeIn (block, predicate) {
  if (!block || !block.nodes) {
    return null
  }

  for (const node of block.nodes) {
    if (predicate(node)) {
      return node
    }

    const found = findNodeIn(node.block, predicate)

    if (found) {
      return found
    }
  }

  return null
}

export function findNode (render, predicate) {
  if (!predicate) {
    throw new Error('find node requires predicate')
  }

  if (typeof predicate === 'string') {
    const id = predicate

    predicate = node => node.id === id
  }

  if (render.layout?.block) {
    const found = findNodeIn(render.layout.block, predicate)

    if (found) {
      return found
    }
  }

  for (const [, block] of Object.entries(render.branches)) {
    const found = findNodeIn(block, predicate)

    if (found) {
      return found
    }
  }

  for (const [, blockId] of Object.entries(render.links)) {
    const block = findBlock(render, blockId)

    if (!block) {
      continue
    }

    const found = findNodeIn(block, predicate)

    if (found) {
      return found
    }
  }

  for (const [, block] of Object.entries(render.blocks)) {
    const found = findNodeIn(block, predicate)

    if (found) {
      return found
    }
  }
}
